import { Link } from "react-router-dom";
import { Mail, Instagram, Twitter, Linkedin, MapPin, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useSettings, FALLBACK_EMAIL } from "@/lib/api";
import { NAV_SERVICES } from "@/data/site";
import { LOCATION_LINKS, INDUSTRY_LINKS } from "@/data/landing";

const Col = ({ title, children }) => (
  <div>
    <p className="eyebrow text-violet-300">{title}</p>
    <ul className="mt-5 space-y-3 text-sm text-violet-100">{children}</ul>
  </div>
);
const FLink = ({ to, children }) => (
  <li><Link to={to} className="transition-colors hover:text-white">{children}</Link></li>
);

export const Footer = () => {
  const s = useSettings();
  const email = s.contact_email || FALLBACK_EMAIL;
  const socials = [[s.instagram_url, Instagram, "Instagram"], [s.twitter_url, Twitter, "X / Twitter"], [s.linkedin_url, Linkedin, "LinkedIn"]].filter(([u]) => u);
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-[#0C0320] pb-28 pt-20 text-white md:pb-12">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1fr]">
        <div>
          <Logo className="h-16" />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-violet-100">
            Experienced digital marketing expertise, tailored to your business, with measurable results and a genuinely personal service.
          </p>
          <a href={`mailto:${email}`} data-testid="footer-email" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-white hover:text-violet-300">
            <Mail className="h-4 w-4" /> {email}
          </a>
          {s.whatsapp_number && (
            <a href={`https://wa.me/${String(s.whatsapp_number).replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
              className="mt-3 flex items-center gap-2 text-sm font-bold text-white hover:text-[#25D366]">
              <MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp {String(s.whatsapp_number).replace(/^\+?44/, "0")}
            </a>
          )}
          <p className="mt-3 flex items-center gap-2 text-sm text-violet-100"><MapPin className="h-4 w-4" /> {s.location || "Tunbridge Wells, Kent, United Kingdom"}</p>
          <div className="mt-6 flex gap-2">
            {socials.map(([u, I, label]) => (
              <a key={label} href={u} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`footer-social-${label.split(" ")[0].toLowerCase()}`}
                className="rounded-full border border-white/15 p-2.5 transition-colors hover:border-violet-400 hover:bg-white/5"><I className="h-4 w-4" /></a>
            ))}
          </div>
        </div>
        <Col title="Services">{NAV_SERVICES.map(([slug, t]) => <FLink key={slug} to={`/${slug}`}>{t}</FLink>)}</Col>
        <Col title="Company">
          <FLink to="/about-freya-jones">About Freya Jones</FLink>
          <FLink to="/results">Results & Case Studies</FLink>
          <FLink to="/pricing">Pricing</FLink>
          <FLink to="/blog">Blog & Insights</FLink>
          <FLink to="/contact">Contact</FLink>
        </Col>
        <Col title="Industries">
          {INDUSTRY_LINKS.map(([to, t]) => <FLink key={to} to={to}>{t}</FLink>)}
        </Col>
        <Col title="Areas served">
          {LOCATION_LINKS.map(([to, t]) => <FLink key={to} to={to}>{t}</FLink>)}
          <li className="text-violet-200">Also East Sussex, West Sussex, Surrey, London and UK-wide</li>
        </Col>
      </div>
      <div className="mx-auto mt-16 flex max-w-7xl flex-col gap-4 border-t border-white/10 px-5 pt-8 text-xs text-violet-200 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <p>© {new Date().getFullYear()} Digital AdDoctor · Freya Jones. All rights reserved.</p>
        <div className="flex flex-wrap gap-5">
          <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-white">Privacy Policy</Link>
          <Link to="/cookie-policy" data-testid="footer-cookies" className="hover:text-white">Cookie Policy</Link>
          <button data-testid="footer-cookie-settings" onClick={() => window.dispatchEvent(new Event("dad-open-consent"))} className="hover:text-white">Cookie settings</button>
          <Link to="/admin" data-testid="footer-admin" className="hover:text-white">Admin</Link>
        </div>
      </div>
    </footer>
  );
};
